import { Button, Icon } from '@zillow/constellation-10';
import { IconTourDoorFilled } from '@zillow/constellation-icons';

import { LoadingHeading } from '@zg-rentals/loading-components';
import {
  ToursSummary,
  UpcomingTours,
  useHasFutureTourAvailability,
  useListingTourConfig,
  useTouringTreatmentForZuidAndListing,
} from '@zg-rentals/touring';

import { useScreenSize } from '../../../../hooks/useScreenSize';

import accessUtils from '../../../../utils/accessUtils';

import { getTourCalendarRoute } from '../../../../constants/urls';

import { useAuth } from '../../../../components/AuthProvider';
import EmptyState from '../../../../components/EmptyState';

import {
  emitTourSchedulingUpsellSetTourAvailabilityCtaEvent,
  emitUpcomingToursMoreToursCtaEvent,
  TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID,
  UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID,
} from '../../../../analytics/events/overviewPage';
import ConnectedModuleCard from '../../../ConnectedModuleCard';

type Props = {
  listingAlias: string;
  isActive: boolean;
};

const UpcomingToursModule = ({ listingAlias, isActive }: Props) => {
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const inquiriesPath = `/rental-manager/lead-management/${listingAlias}`;
  const { pathname: tourCalendarPathname } = getTourCalendarRoute({ listingAlias });
  const tourCalendarPath = `/rental-manager${tourCalendarPathname.replace('[listingAlias]', listingAlias)}`;

  const isRedesignEnabled = accessUtils.canAccessUpcomingToursRedesign();

  const { data: touringTreatment, isLoading: isGetTouringTreatmentLoading } = useTouringTreatmentForZuidAndListing(
    { listingAlias },
    { enabled: Boolean(listingAlias), errorDetails: 'UpcomingToursModule - Error fetching touring treatment' },
  );

  const isTouringEnabled = Boolean(touringTreatment?.isTouringEnabledForListing);

  const { hasFutureAvailability, isLoading: isAvailabilityLoading } = useHasFutureTourAvailability(listingAlias, {
    enabled: !isRedesignEnabled && isTouringEnabled,
  });

  // Shares a query key with the widget's own call, so this gate costs no extra request.
  const { hasTourConfig } = useListingTourConfig(listingAlias, { enabled: isRedesignEnabled });

  const onCalendarClick = () => {
    if (user) {
      emitUpcomingToursMoreToursCtaEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const handleTourSchedulingUpsellClick = () => {
    if (user) {
      emitTourSchedulingUpsellSetTourAvailabilityCtaEvent({ currentProperty: { listingAlias }, user });
    }
  };

  // The widget owns its loading, error and empty states, so it mounts on the first render: its requests
  // run alongside the ones above instead of queueing behind a preflight. Only a confirmed negative
  // hides the card, so a slow or failed gate request leaves the widget in place rather than blanking it.
  if (isRedesignEnabled) {
    const isUntoured = hasTourConfig === false;
    // Touring being off for the listing sends the tour calendar to tour-availability, which drops the
    // deep-linked tourId, so the card stays hidden there just as it is on the flag-off path.
    const isTouringOffForListing = Boolean(touringTreatment) && !isTouringEnabled;
    // Inactive listings keep the card only while tours are on the books, so they wait for the treatment
    // to confirm it — rendering sooner would flash the card and hide it once the treatment resolves.
    const isInactiveWithoutBookedTours = !isActive && !touringTreatment?.hasBookedTours;

    if (isUntoured || isTouringOffForListing || isInactiveWithoutBookedTours) {
      return null;
    }

    return (
      <ConnectedModuleCard css={{ borderRadius: 'obj.default' }}>
        <ToursSummary
          listingAlias={listingAlias}
          tourCalendarPath={tourCalendarPath}
          showCancelledTours={accessUtils.canShowCancelledTours()}
        />
      </ConnectedModuleCard>
    );
  }

  const isLoading = isGetTouringTreatmentLoading || (isTouringEnabled && isAvailabilityLoading);

  if (isLoading) {
    return (
      <ConnectedModuleCard
        isLoading
        css={{
          borderRadius: 'obj.default',
        }}
      >
        <LoadingHeading level={2} isLoading loadingWidth="100%">
          Upcoming Tours
        </LoadingHeading>
      </ConnectedModuleCard>
    );
  }

  if (!touringTreatment || !isTouringEnabled || !(isActive || touringTreatment.hasBookedTours)) {
    return null;
  }

  // Touring is enabled, but with no upcoming availability and no booked tours the "Upcoming tours"
  // widget would render empty and misleading — prompt the user to set availability instead.
  const hasUpcomingContent = hasFutureAvailability || touringTreatment.hasBookedTours;

  return (
    <ConnectedModuleCard
      css={{
        borderRadius: 'obj.default',
      }}
    >
      {hasUpcomingContent ? (
        <UpcomingTours
          inquiriesPath={inquiriesPath}
          isScreenMobile={isScreenMobile}
          listingAlias={listingAlias}
          onCalendarClick={onCalendarClick}
          tourCalendarPath={tourCalendarPath}
          eventId={UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID}
        />
      ) : (
        <EmptyState
          data-testid="EmptyUpcomingToursCard"
          icon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconTourDoorFilled />} />}
          heading="No tours scheduled yet"
          body="Set your tour availability so renters can book a tour."
          align="left"
          useCard={false}
          actions={
            <Button tone="brand" emphasis="filled" asChild>
              <a
                href={tourCalendarPath}
                onClick={handleTourSchedulingUpsellClick}
                data-eventid={TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID}
              >
                Set tour availability
              </a>
            </Button>
          }
        />
      )}
    </ConnectedModuleCard>
  );
};

export default UpcomingToursModule;
